// @ts-nocheck
import * as React from "react";
import { Ic } from "../screens/common";
import { formatWait, classifyUrgency, URGENCY_ACCENT } from "./waitTime";

// Left-side inbox listing pending matching-stage cases for the COD screen.
// Pure presentational — the parent owns fetching, selection state,
// polling, and the seen/priority/search state. Selecting a row notifies
// the parent via onSelect(caseId).

const PRIORITY_BADGE = {
  critical: { color: "rose",  short: "높음" },
  warning:  { color: "amber", short: "보통" },
  normal:   { color: "gray",  short: "낮음" },
};

// Chip group for priority filtering. "all" is the default — emergency
// mode collapses to picking "critical" here.
const PRIORITY_FILTERS = [
  { key: "all",      label: "전체",  color: "ink-3" },
  { key: "critical", label: "높음",  color: "rose-500" },
  { key: "warning",  label: "보통",  color: "amber-500" },
  { key: "normal",   label: "낮음",  color: "ink-3" },
];

// Lowercased haystack for an entry. Computed once per entry rather than
// per keystroke so search stays O(n) in the queue size.
function searchHaystack(entry) {
  const c  = entry.case || {};
  const e  = c.elderly || {};
  const tm = entry.top_match;
  return [
    e.name,
    tm?.care_manager?.name,
    tm?.care_manager?.qualification,
    String(c.id),
  ].filter(Boolean).join("|").toLowerCase();
}

export function CaseInbox({
  entries,
  total,
  selectedCaseId,
  loading,
  err,
  onSelect,
  onRefresh,
  priorityFilter = "all",
  onChangePriorityFilter,
  query = "",
  onChangeQuery,
  seenSet,         // Set<string> — case ids the operator has already opened
}) {
  // Per-entry haystack cache keyed by entry identity. Stays cheap even
  // when the user types fast, because the underlying entries array doesn't
  // change between keystrokes — only the query does.
  const haystacks = React.useMemo(() => entries.map(searchHaystack), [entries]);
  const needle = (query || "").trim().toLowerCase();

  const filtered = React.useMemo(() => {
    return entries.filter((e, i) => {
      if (priorityFilter !== "all" && e.case?.priority !== priorityFilter) return false;
      if (needle && !haystacks[i].includes(needle)) return false;
      return true;
    });
  }, [entries, haystacks, priorityFilter, needle]);

  // Per-priority counts powering the chip badges. Skipping useMemo here
  // would be fine — the inbox sizes are bounded — but it keeps the JSX
  // free of inline reduce calls.
  const counts = React.useMemo(() => {
    const out = { all: entries.length, critical: 0, warning: 0, normal: 0 };
    for (const e of entries) {
      const p = e.case?.priority;
      if (p === "critical" || p === "warning" || p === "normal") out[p]++;
    }
    return out;
  }, [entries]);

  const unseenInFiltered = React.useMemo(() => {
    if (!seenSet) return 0;
    return filtered.reduce((n, e) => n + (seenSet.has(String(e.case.id)) ? 0 : 1), 0);
  }, [filtered, seenSet]);

  return (
    <aside className="card" style={{display:'flex', flexDirection:'column', minHeight:0, overflow:'hidden'}}>
      <div style={{padding:'14px 16px 10px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
        <div>
          <div style={{fontSize:13, fontWeight:800}}>처리 대기 케이스</div>
          <div className="muted" style={{fontSize:11, marginTop:2}}>
            {loading && entries.length === 0
              ? '불러오는 중…'
              : (priorityFilter !== "all" || needle)
                ? `${filtered.length}건 표시 / 전체 ${total ?? entries.length}건`
                : `${total ?? entries.length}건 · 위험 우선 정렬`}
            {unseenInFiltered > 0 && (
              <span style={{color:'var(--orange-600)', fontWeight:700, marginLeft:6}}>
                · 새 {unseenInFiltered}건
              </span>
            )}
          </div>
        </div>
        {onRefresh && (
          <button type="button" onClick={onRefresh} className="btn btn-ghost btn-sm"
            aria-label="목록 새로고침" title="목록 새로고침"
            style={{padding:4}}>
            ↻
          </button>
        )}
      </div>

      {/* Search box. Sticky-feel: keyed at the top so the operator's
          fingers don't need to scroll up to filter further. */}
      {onChangeQuery && (
        <div style={{padding:'10px 12px 6px'}}>
          <input
            type="search"
            value={query}
            onChange={e=>onChangeQuery(e.target.value)}
            placeholder="어르신·매니저·사례번호 검색"
            aria-label="인박스 검색"
            className="input"
            style={{padding:'7px 10px', fontSize:12, width:'100%'}}
          />
        </div>
      )}

      {/* Priority filter chips */}
      {onChangePriorityFilter && (
        <div style={{padding:'4px 12px 8px', display:'flex', gap:4, flexWrap:'wrap'}}>
          {PRIORITY_FILTERS.map(f => {
            const active = priorityFilter === f.key;
            return (
              <button key={f.key} type="button"
                onClick={()=>onChangePriorityFilter(f.key)}
                aria-pressed={active}
                style={{
                  padding:'4px 9px', fontSize:11, fontWeight:700,
                  borderRadius:99,
                  border:'1px solid ' + (active ? `var(--${f.color})` : 'var(--border-soft)'),
                  background: active ? `var(--${f.color === 'ink-3' ? 'bg-sub' : f.color.replace('-500', '-50')})` : 'var(--surface)',
                  color: active ? (f.color === 'ink-3' ? 'var(--ink-1)' : `var(--${f.color})`) : 'var(--ink-3)',
                  cursor:'pointer',
                  display:'inline-flex', alignItems:'center', gap:5,
                }}>
                <span>{f.label}</span>
                <span className="mono" style={{fontSize:10, fontWeight:700, opacity:0.85}}>{counts[f.key]}</span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{flex:1, overflowY:'auto'}}>
        {err && <InboxMessage tone="error">불러오기 실패: {err}</InboxMessage>}
        {!err && !loading && filtered.length === 0 && entries.length > 0 && (priorityFilter !== "all" || needle) && (
          <InboxMessage>
            조건에 맞는 케이스가 없습니다.
            {(onChangePriorityFilter || onChangeQuery) && (
              <div style={{marginTop:10}}>
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={()=>{ onChangePriorityFilter?.("all"); onChangeQuery?.(""); }}>
                  필터 초기화
                </button>
              </div>
            )}
          </InboxMessage>
        )}
        {!err && !loading && entries.length === 0 && (
          <InboxMessage>처리 대기 중인 매칭이 없습니다.</InboxMessage>
        )}
        {filtered.map(entry => (
          <InboxRow
            key={entry.case.id}
            entry={entry}
            selected={String(entry.case.id) === String(selectedCaseId)}
            seen={seenSet?.has(String(entry.case.id)) ?? true}
            onSelect={onSelect}
          />
        ))}
      </div>
    </aside>
  );
}

function InboxRow({ entry, selected, seen, onSelect }) {
  const { case: c, waited_seconds } = entry;
  const pb = PRIORITY_BADGE[c.priority] || PRIORITY_BADGE.normal;
  const urgency = classifyUrgency(c.priority, waited_seconds);
  const accent  = URGENCY_ACCENT[urgency];
  const elderly = c.elderly;
  const showNew = !seen && !selected;

  return (
    <button type="button"
      onClick={()=>onSelect(c.id)}
      aria-pressed={selected}
      style={{
        display:'block', width:'100%', textAlign:'left',
        padding:'12px 16px',
        borderTop:'1px solid var(--border-soft)',
        background: selected ? 'var(--orange-50)' : 'transparent',
        borderLeft: selected ? '3px solid var(--orange-500)' : '3px solid transparent',
        border: 'none', borderRadius: 0, cursor: 'pointer',
        opacity: seen && !selected ? 0.7 : 1,
        transition: 'background 0.12s, opacity 0.12s',
      }}>
      <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
        <span className={"badge badge-" + pb.color} style={{fontSize:10, padding:'1px 6px'}}>{pb.short}</span>
        {showNew && (
          <span style={{
            padding:'1px 6px', fontSize:9, fontWeight:800, letterSpacing:'0.06em',
            background:'var(--orange-500)', color:'#fff', borderRadius:99,
          }}>
            NEW
          </span>
        )}
        <span style={{
          marginLeft:'auto', fontSize:11, fontWeight:700,
          color: accent.text, display:'inline-flex', alignItems:'center', gap:4,
        }}>
          <span style={{width:6, height:6, borderRadius:99, background: accent.dot, display:'inline-block'}}/>
          {formatWait(waited_seconds)} 대기
        </span>
      </div>
      <div style={{fontSize:13.5, fontWeight: showNew ? 800 : 700, color:'var(--ink-1)'}}>
        {elderly?.name || `사례 #${c.id}`}
        {elderly?.age != null && <span className="muted" style={{fontWeight:500, fontSize:11, marginLeft:6}}>{elderly.age}세</span>}
      </div>
    </button>
  );
}

function InboxMessage({ children, tone }) {
  return (
    <div className="muted" style={{
      padding: '30px 16px', textAlign: 'center', fontSize: 12,
      color: tone === 'error' ? 'var(--rose-500)' : 'var(--ink-3)',
    }}>
      {children}
    </div>
  );
}
